import { Divider, Flex, Form, Skeleton, Typography } from "antd";
import { NOTIFICATION_TYPE_ENUM, OPERATION_TYPE, QuoteAcceptData } from "interfaces";
import { observer } from "mobx-react-lite";
import { useCallback, useEffect, useRef, useState } from "react";

import { CustomButton, OtpInput, OtpTimeLeft } from "ui";
import { useStores } from "stores";
import { clearCertDB, signatureMessage } from "utils/processCert";
import { checkDaysPassed, hashMessage, notificationPeriodDays } from "utils";
import { DIRECT_PAYMENT_CUSTOMER_LP, SMS_CODE_RETRY_DELAY_IN_SECONDS } from "utils/envConstants";
import { createBidOperation, generateOperation, RelevantOperationType } from "services";
import { canSendSms, clearSmsData, getLastSmsData, markSmsSend, parseErrorMessage } from "utils";
import { PageMessage, defaultMessage } from "components/PageMessage/PageMessage";
import { useNotificationContext } from "providers";

enum SEND_STEP {
  SEND = "SEND",
  SMS = "SMS",
}

interface IProps {
  operationType: RelevantOperationType;
  modalTitle: string;
  messageTitle: string;
  tooltip: string;
  quoteRequestData?: QuoteAcceptData;
}

export const BidOperationModal = observer((props: IProps) => {
  const { actionModals, bidCard, user, bankAccountsStore } = useStores();
  const [loading, setLoading] = useState(false);
  const [sendStep, setSendStep] = useState(SEND_STEP.SEND);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timeLeftInterval, setTimeLeftInterval] = useState<NodeJS.Timeout>();
  const [currentMessageHash, setCurrentMessageHash] = useState<string>("");
  const { openNotification } = useNotificationContext();

  const { isSeniorLP, isJuniorLP, isCustomer, isOperator, isSeniorOperator } = user.roles;
  const isLpProvider = isJuniorLP || isSeniorLP;

  const isSeniorOperatorDaysPassed =
    checkDaysPassed(bidCard.bid.created, notificationPeriodDays).isOverDays &&
    !bidCard.bid.requestProcessing.fiatReceived &&
    bidCard.bid.requestProcessing.rubReceivedByPlatform &&
    isSeniorOperator;

  const isShowBankAccount =
    !DIRECT_PAYMENT_CUSTOMER_LP &&
    bidCard.bid.status === "FUNDING" &&
    (((isCustomer || isSeniorOperatorDaysPassed) && bidCard.bid.contract.direction === "EXP") ||
      (isLpProvider && bidCard.bid.contract.direction === "IMP"));
  const bankAccountId = isShowBankAccount ? bankAccountsStore.selectedAccountId : null;
  const defaultAccountId = isShowBankAccount ? bankAccountsStore.defaultAccountId : null;
  const isSubmitDisabled = loading || (isShowBankAccount && !(bankAccountId || bankAccountsStore.defaultAccountId));

  useEffect(() => {
    return () => {
      if (timeLeftInterval) {
        clearInterval(timeLeftInterval);
      }
    };
  }, [timeLeftInterval]);

  const [form] = Form.useForm();

  let numberPost = useRef(0);

  const destinationWalletForMessage =
    props.operationType === OPERATION_TYPE.REQUEST_APPROVE && bidCard.bid.contract.direction === "IMP"
      ? bidCard.bid.destinationWallet
      : "";

  const generateMessage = useCallback(() => {
    const message = defaultMessage({
      bid: bidCard.bid,
      user,
      rate: bidCard.bid.quote?.rate.toString() || "",
      destinationWallet: destinationWalletForMessage,
      operationModal: true,
      quoteRequestData: props.quoteRequestData,
    });

    return message;
  }, [bidCard.bid, destinationWalletForMessage, user, props.quoteRequestData]);

  const generateSignature = useCallback(
    async (currentMessage?: Record<string, any>) => {
      const message = currentMessage ?? generateMessage();

      const { signature: newSignature, jsonMessage } = await signatureMessage({
        userId: user.userInfo?.id,
        companyId: user.userInfo?.company_id,
        message,
        user,
        actionModals,
      });

      const messageHash = await hashMessage(jsonMessage);

      return {
        signature: newSignature,
        message: jsonMessage,
        hash: messageHash,
      };
    },
    [actionModals, user, generateMessage]
  );

  const startOtpCountdown = useCallback(() => {
    if (timeLeftInterval) {
      clearInterval(timeLeftInterval);
      setTimeLeftInterval(undefined);
    }
    setTimeLeft(SMS_CODE_RETRY_DELAY_IN_SECONDS);
    const interval = setInterval(() => {
      setTimeLeft((prev) => {
        const next = prev - 1;
        if (next <= 0) {
          clearInterval(interval);
          setTimeLeftInterval(undefined);
          return 0;
        }
        return next;
      });
    }, 1000);
    setTimeLeftInterval(interval);
    setSendStep(SEND_STEP.SMS);

    form.setFieldsValue({
      otp: "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeLeftInterval]);

  const sendSms = useCallback(async () => {
    let canSendSmsCode = canSendSms(props.operationType);

    if (canSendSmsCode) {
      setLoading(true);
      const signatureData = await generateSignature();
      setCurrentMessageHash(signatureData.hash);
      const formData = JSON.stringify({ message: signatureData.message });
      await generateOperation(props.operationType, formData)
        .then(() => {
          if (signatureData) {
            markSmsSend(props.operationType, signatureData.message);
          }
          startOtpCountdown();
        })
        .catch((e) => {
          if (e.type === "/otp-error") {
            startOtpCountdown();
            openNotification({
              description: e.title || `Код можно отправить раз в ${SMS_CODE_RETRY_DELAY_IN_SECONDS} секунд`,
              type: NOTIFICATION_TYPE_ENUM.ERROR,
            });
          } else {
            openNotification({
              description: parseErrorMessage(e),
              type: NOTIFICATION_TYPE_ENUM.ERROR,
            });
            actionModals.closeAll();
          }
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
      const message = getLastSmsData(props.operationType);
      if (!message) {
        openNotification({
          description: "Не удалось найти предыдущий message, попробуйте позже",
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      } else {
        openNotification({
          description: `Код можно отправить раз в ${SMS_CODE_RETRY_DELAY_IN_SECONDS} секунд`,
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
        startOtpCountdown();
      }
    }
  }, [generateSignature, props.operationType, startOtpCountdown, openNotification]);

  const sendBid = async () => {
    setLoading(true);
    const values = form.getFieldsValue();
    const message = getLastSmsData(props.operationType);
    const signatureData = await generateSignature(JSON.parse(message?.message ?? ""));

    const formData = JSON.stringify({
      otp: values.otp,
      ...signatureData,
      externalRecipientAccount: (bankAccountId || bankAccountsStore.defaultAccountId?.id) ?? undefined,
    });

    await createBidOperation(
      {
        id: bidCard.bid.id,
        operationType: props.operationType,
        quoteRequestData: props.quoteRequestData?.quoteRequest ? props.quoteRequestData : undefined,
      },
      formData
    )
      .then(() => {
        form.resetFields();
        clearSmsData(props.operationType);
        openNotification({
          message: "Запрос успешно обработан",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        handleCancelSend();
      })
      .catch(async (e) => {
        if (e.title === "Недействительная подпись" || e.title === "Invalid signature") {
          if (numberPost.current === 0) {
            numberPost.current++;
            await clearCertDB(user.userInfo?.id, user.userInfo?.company_id);
            sendBid();
          } else {
            actionModals.closeAll();
            user.error = "Сервис временно неисправен, попробуйте позже";
            user.logout();
          }
        } else {
          openNotification({
            description: parseErrorMessage(e),
            type: NOTIFICATION_TYPE_ENUM.ERROR,
          });
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const roles = isCustomer || isOperator || isLpProvider;

  const shouldSendSms = () => {
    if (!roles) {
      return false;
    }

    if (DIRECT_PAYMENT_CUSTOMER_LP) {
      return true;
    }

    const { direction } = bidCard.bid.contract;
    const { status } = bidCard.bid;
    const isFunding = status === "FUNDING";

    if (!isFunding) {
      return true;
    }

    if (isCustomer) {
      return direction === "EXP" || direction === "IMP";
    }

    if (isLpProvider) {
      return direction === "IMP" || direction === "EXP";
    }

    if (isOperator) {
      return direction === "EXP";
    }
  };

  useEffect(() => {
    if (!roles || !bidCard) return;

    if (shouldSendSms()) {
      sendSms();
    }
  }, [roles, isCustomer, isOperator, isLpProvider, bidCard.bid.contract.direction, bidCard.bid.status]);

  const handleCancelSend = () => {
    if (timeLeftInterval) {
      clearInterval(timeLeftInterval);
    }
    setTimeLeft(0);
    setSendStep(SEND_STEP.SEND);
    actionModals.closeLastModal();
    bankAccountsStore.selectedAccountId = undefined;
  };
  return (
    <>
      <Flex vertical>
        {loading ? (
          <Flex vertical gap="small">
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
            <Skeleton active />
          </Flex>
        ) : (
          <Form
            form={form}
            name="sendBid"
            initialValues={{
              otp: "",
            }}
            className="full-width"
            onFinish={() => sendBid()}
          >
            <Flex vertical>
              <Typography.Title level={5} className="margin-none">
                {props.messageTitle}
              </Typography.Title>
              <PageMessage
                bid={bidCard.bid}
                user={user}
                rate={bidCard.bid.quote?.rate.toString() || ""}
                destinationWallet={destinationWalletForMessage}
                messageHash={currentMessageHash}
                showLpInfo={isLpProvider}
                operationModal={true}
                quoteRequestData={props.quoteRequestData}
              />
            </Flex>
            <Divider />

            {sendStep === SEND_STEP.SMS && (
              <Flex vertical gap="small">
                <Typography.Title level={5} className="margin-none text-center">
                  Подтвердите действие кодом из смс:
                </Typography.Title>

                <Flex vertical>
                  <OtpInput disabled={loading} className="margin-top-xs" onSendSms={sendSms} loading={loading} />
                  <OtpTimeLeft onResend={sendSms} loading={loading} shouldStartCountdown={!!timeLeftInterval} />

                  <Divider />
                </Flex>
              </Flex>
            )}

            <Form.Item>
              <Flex gap="large">
                <CustomButton
                  htmlType="submit"
                  disabled={isSubmitDisabled}
                  loading={loading}
                  tooltip={sendStep === SEND_STEP.SMS ? props.tooltip : undefined}
                >
                  {sendStep === SEND_STEP.SMS ? "Подписать Электронной подписью" : "Подписать"}
                </CustomButton>
                <CustomButton
                  htmlType="button"
                  disabled={loading}
                  loading={loading}
                  type="default"
                  onClick={handleCancelSend}
                >
                  Отмена
                </CustomButton>
              </Flex>
            </Form.Item>
          </Form>
        )}
      </Flex>
    </>
  );
});
